import { getSafeAreaInsets } from '@/core/guidance/clamp-hint-anchor-rect';

const FOCUS_MARGIN_PX = 12;
/**
 * Room kept free below the focused field so the next fields of a form stay
 * visible and tappable (DEV-519): the next two fields when they can be
 * measured, otherwise a share of the visible band above the keyboard.
 */
const COMFORT_NEXT_FIELDS = 2;
const COMFORT_BAND_RATIO = 0.4;
const COMFORT_MAX_PX = 200;
/** Measured room never takes more than this share of the visible band. */
const COMFORT_MAX_BAND_RATIO = 0.6;

const NON_TEXT_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

export function isEditableField(element: Element): element is HTMLElement {
  if (!(element instanceof HTMLElement)) return false;
  if (element.isContentEditable) return true;

  const tag = element.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag !== 'INPUT') return false;

  const type = (element as HTMLInputElement).type || 'text';
  return !NON_TEXT_INPUT_TYPES.has(type);
}

/** Skip inputs inside bottom sheets — drawer handles its own keyboard offset. */
export function isInsideBottomSheet(element: HTMLElement): boolean {
  return Boolean(
    element.closest('[data-vaul-drawer], [data-vaul-drawer-wrapper]'),
  );
}

function findOverflowScrollParent(element: HTMLElement): HTMLElement | null {
  const wizardScroll = element.closest(
    '[data-wizard-inner-scroll]',
  ) as HTMLElement | null;
  if (
    wizardScroll &&
    wizardScroll.scrollHeight > wizardScroll.clientHeight + 1
  ) {
    return wizardScroll;
  }

  let parent = element.parentElement;

  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    const scrollable =
      overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';

    if (scrollable && parent.scrollHeight > parent.clientHeight + 1) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return null;
}

async function scrollIonContentBy(
  ionContent: HTMLIonContentElement,
  deltaY: number,
): Promise<void> {
  const scrollEl = await ionContent.getScrollElement();
  const targetTop = Math.max(0, scrollEl.scrollTop + deltaY);

  if (typeof ionContent.scrollToPoint === 'function') {
    await ionContent.scrollToPoint(0, targetTop, 150);
    return;
  }

  scrollEl.scrollTop = targetTop;
}

async function scrollElementBy(
  element: HTMLElement,
  deltaY: number,
): Promise<void> {
  if (Math.abs(deltaY) < 1) return;

  const ionContent = element.closest('ion-content');
  if (ionContent) {
    try {
      // Wizards deliberately disable IonContent scrolling and use an inner
      // overflow container. Respect that and avoid scrolling the whole page.
      if (ionContent.scrollY === false) {
        throw new Error('IonContent scrolling disabled');
      }
      const scrollEl = await ionContent.getScrollElement();
      if (scrollEl.scrollHeight > scrollEl.clientHeight + 1) {
        await scrollIonContentBy(ionContent, deltaY);
        return;
      }
    } catch {
      // IonContent may not expose a scroll element; use the overflow parent.
    }
  }

  const overflowParent = findOverflowScrollParent(element);
  if (overflowParent) {
    overflowParent.scrollTop = Math.max(0, overflowParent.scrollTop + deltaY);
    return;
  }

  element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
}

/**
 * Top edge of whatever `scrollElementBy` will scroll (same resolution order):
 * a field must not be scrolled above it, e.g. under a wizard's own header
 * that sits above its inner scroll container.
 */
function getScrollContainerTop(element: HTMLElement): number {
  const ionContent = element.closest('ion-content');
  if (ionContent && ionContent.scrollY !== false) {
    return ionContent.getBoundingClientRect().top;
  }
  const overflowParent = findOverflowScrollParent(element);
  return overflowParent ? overflowParent.getBoundingClientRect().top : 0;
}

/**
 * Space from the focused field's bottom to the bottom of the next
 * `COMFORT_NEXT_FIELDS` visible fields below it in the same form, or
 * `undefined` when there are none (last field).
 */
function measureNextFieldsRoom(element: HTMLElement): number | undefined {
  const scope =
    element.closest('form') ??
    element.closest('[data-wizard-inner-scroll]') ??
    element.closest('ion-content');
  if (!scope) return undefined;

  const fieldBottom = element.getBoundingClientRect().bottom;
  const below: DOMRect[] = [];
  const candidates = scope.querySelectorAll<HTMLElement>(
    'input, textarea, select, [contenteditable="true"]',
  );
  for (const candidate of candidates) {
    if (below.length >= COMFORT_NEXT_FIELDS) break;
    if (candidate === element || !isEditableField(candidate)) continue;
    if (
      !(
        element.compareDocumentPosition(candidate) &
        Node.DOCUMENT_POSITION_FOLLOWING
      )
    ) {
      continue;
    }
    if ((candidate as HTMLInputElement).disabled) continue;
    const rect = candidate.getBoundingClientRect();
    // Hidden (display: none) or beside the field rather than below it.
    if (rect.height === 0 || rect.top < fieldBottom) continue;
    below.push(rect);
  }
  const last = below.at(-1);
  if (!last) return undefined;
  return Math.round(last.bottom - fieldBottom) + FOCUS_MARGIN_PX;
}

function getFloatingWizardCtaHeight(): number {
  const footer = document.querySelector('[data-wizard-floating-cta]');
  if (!(footer instanceof HTMLElement)) return 0;
  const height = footer.getBoundingClientRect().height;
  return height > 0 ? Math.round(height) : 0;
}

export type FocusScrollGeometry = {
  fieldTop: number;
  fieldBottom: number;
  viewportHeight: number;
  keyboardHeight: number;
  /** Lowest y the field's top may be scrolled to (safe area / container). */
  visibleTop: number;
  /** Extra chrome above the keyboard (e.g. floating wizard CTA). */
  bottomReserve?: number;
  /** Measured room for the next fields; defaults to a share of the band. */
  comfortPx?: number;
};

/**
 * How far to scroll down so the focused field clears the keyboard with
 * comfortable room below it. Returns 0 when no scroll is needed.
 *
 * Always clears the keyboard (as before); the extra comfort room is capped
 * so the field's top never moves above `visibleTop`, and it never scrolls up.
 */
export function computeFocusScrollDelta({
  fieldTop,
  fieldBottom,
  viewportHeight,
  keyboardHeight,
  visibleTop,
  bottomReserve = 0,
  comfortPx,
}: FocusScrollGeometry): number {
  const visibleBottom =
    viewportHeight - keyboardHeight - FOCUS_MARGIN_PX - bottomReserve;
  const band = Math.max(0, visibleBottom - visibleTop);
  const comfort =
    comfortPx === undefined
      ? Math.min(COMFORT_MAX_PX, Math.round(band * COMFORT_BAND_RATIO))
      : Math.min(comfortPx, Math.round(band * COMFORT_MAX_BAND_RATIO));

  const mustScroll = fieldBottom - visibleBottom;
  const comfortScroll = Math.min(
    fieldBottom - (visibleBottom - comfort),
    fieldTop - visibleTop,
  );
  const delta = Math.max(mustScroll, comfortScroll);
  return delta >= 1 ? delta : 0;
}

/**
 * Scrolls a plain HTML input above the keyboard, leaving room below it.
 *
 * With `Keyboard.resize: 'none'`, the WebView does not shrink — use the native
 * keyboard height only (not visualViewport, which is unreliable on iOS here).
 */
export async function scrollFocusedInputIntoView(
  element: HTMLElement,
  keyboardHeight: number,
): Promise<void> {
  if (keyboardHeight <= 0) return;
  if (isInsideBottomSheet(element)) return;

  const rect = element.getBoundingClientRect();
  const delta = computeFocusScrollDelta({
    fieldTop: rect.top,
    fieldBottom: rect.bottom,
    viewportHeight: window.innerHeight,
    keyboardHeight,
    visibleTop:
      Math.max(getSafeAreaInsets().top, getScrollContainerTop(element)) +
      FOCUS_MARGIN_PX,
    bottomReserve: getFloatingWizardCtaHeight(),
    comfortPx: measureNextFieldsRoom(element),
  });

  if (delta > 0) {
    await scrollElementBy(element, delta);
  }
}
